import { useEffect, useRef, useState } from 'react';
import { streem } from 'streem-sdk-protobuf';
import { ACTIVE_MODAL_MESSAGES } from '../util/group_reservation_helpers';
import appLogger from '../../../util/logging/app_logger';
import { StreemApiUserAvailabilityAvailabilityStatus } from '@streem/api';
import { UPDATE_AVAILABILITY_PROMPT_TIME_MS } from '../../../util/constants';

type ModalState = {
    showModal: boolean;
    modalMessage: string;
};

type ExpertAvailabilityModalState = {
    pageLoadModalOpen: boolean;
    needsPageLoadModal: boolean;
    activeModalState: ModalState;
};

type ExpertAvailabilityModalFunctions = {
    closeSetReadyModal: () => void;
    closePageLoadModal: () => void;
    setActive: () => Promise<void>;
    setNotReady: () => Promise<void>;
};

export type ExpertAvailabilityModalReturn = ExpertAvailabilityModalState &
    ExpertAvailabilityModalFunctions;

// For simplicity this duplicates the signature below rather than requiring us to pass in an object during usage.
export interface UseExpertAvailabilityModalsProps {
    userId: string;
    currentAvailabilityStatus: streem.api.AvailabilityStatus;
    updateUserAvailability: (
        status: streem.api.AvailabilityStatus,
    ) => Promise<StreemApiUserAvailabilityAvailabilityStatus>;
}

const AvailabilityStatus = streem.api.AvailabilityStatus;
const log = appLogger.extend('UseExpertAvailabilityModals');

export const useExpertAvailabilityModals = (
    userId: string,
    currentAvailabilityStatus: streem.api.AvailabilityStatus,
    updateUserAvailability: (
        status: streem.api.AvailabilityStatus,
    ) => Promise<StreemApiUserAvailabilityAvailabilityStatus>,
): ExpertAvailabilityModalReturn => {
    const [pageLoadModalOpen, setPageLoadModalOpen] = useState(false);
    const [needsPageLoadModal, setNeedsPageLoadModal] = useState(true);
    const [activeModalState, setActiveModalState] = useState<ModalState>({
        showModal: false,
        modalMessage: ACTIVE_MODAL_MESSAGES.UPDATE_AVAILABILITY_STATUS,
    });

    const previousAvailabilityStatus = useRef<streem.api.AvailabilityStatus | undefined>();
    const timeout = useRef(null);

    /**
     * Set Ready/Active Modal
     */
    useEffect(() => {
        if (currentAvailabilityStatus) {
            // Display a modal in a bad network scenario
            // storing the previous status for when the connection improves
            if (
                previousAvailabilityStatus.current ===
                    AvailabilityStatus.AVAILABILITY_STATUS_CONFIRMING &&
                currentAvailabilityStatus === AvailabilityStatus.AVAILABILITY_STATUS_NOT_AVAILABLE
            ) {
                setActiveModalState({
                    showModal: true,
                    modalMessage: ACTIVE_MODAL_MESSAGES.BAD_NETWORK_STATUS,
                });
            }
            log.info(
                `Received new wall item availability status: ${
                    AvailabilityStatus[currentAvailabilityStatus]
                }, updating previous status: ${
                    AvailabilityStatus[previousAvailabilityStatus.current]
                }`,
            );
            previousAvailabilityStatus.current = currentAvailabilityStatus;
        }

        // Prompt expert to make themselves available with a throttle of 10 minutes
        if (currentAvailabilityStatus === AvailabilityStatus.AVAILABILITY_STATUS_NOT_AVAILABLE) {
            clearTimeout(timeout.current);
            timeout.current = setTimeout(() => {
                setActiveModalState({
                    showModal: true,
                    modalMessage: ACTIVE_MODAL_MESSAGES.UPDATE_AVAILABILITY_STATUS,
                });
            }, UPDATE_AVAILABILITY_PROMPT_TIME_MS);
        }

        // Clear the timeout and update the modal state if the availability is anything other than NOT AVAILABLE
        if (
            currentAvailabilityStatus !== AvailabilityStatus.AVAILABILITY_STATUS_NOT_AVAILABLE &&
            timeout.current
        ) {
            clearTimeout(timeout.current);
            setActiveModalState({
                showModal: false,
                modalMessage: ACTIVE_MODAL_MESSAGES.UPDATE_AVAILABILITY_STATUS,
            });
        }

        // we only want to update when the availability status changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAvailabilityStatus]);

    /**
     * Needs Page Load Modal
     **/
    useEffect(() => {
        // Trigger Page Load Modal when it's determined necessary
        const shouldShowPageLoadModal =
            previousAvailabilityStatus.current === currentAvailabilityStatus &&
            currentAvailabilityStatus === AvailabilityStatus.AVAILABILITY_STATUS_AVAILABLE &&
            needsPageLoadModal;
        const shouldClosePageLoadModal =
            currentAvailabilityStatus !== AvailabilityStatus.AVAILABILITY_STATUS_AVAILABLE &&
            pageLoadModalOpen;

        // only show if we load with status of: Ready
        if (shouldShowPageLoadModal) {
            setPageLoadModalOpen(true);
            log.info(`Showing page load modal for user ${userId}`);
            setNeedsPageLoadModal(false);
        }

        // if the user is no longer in the available status, close the page load modal
        if (shouldClosePageLoadModal) {
            setPageLoadModalOpen(false);
        }

        // clear the need if we've loaded without a need
        if (
            needsPageLoadModal &&
            currentAvailabilityStatus !== AvailabilityStatus.AVAILABILITY_STATUS_AVAILABLE
        ) {
            setNeedsPageLoadModal(false);
        }

        // we only want to update when the availability status changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAvailabilityStatus]);

    const closeSetReadyModal = () =>
        setActiveModalState({
            showModal: false,
            modalMessage: ACTIVE_MODAL_MESSAGES.UPDATE_AVAILABILITY_STATUS,
        });

    const closePageLoadModal = () => {
        log.info(`${userId} has interacted with the page and closed the page load modal`);
        setPageLoadModalOpen(false);
    };

    const setActive = async () => {
        updateUserAvailability(AvailabilityStatus.AVAILABILITY_STATUS_AVAILABLE);
    };

    const setNotReady = async () => {
        updateUserAvailability(AvailabilityStatus.AVAILABILITY_STATUS_NOT_AVAILABLE);
    };

    return {
        needsPageLoadModal,
        pageLoadModalOpen,
        activeModalState,
        closeSetReadyModal,
        closePageLoadModal,
        setActive,
        setNotReady,
    };
};
